import { Component, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { GlobalVariables } from '@parkupp/core'
import { GlobalVariablesService } from '../../../services/global-variables.service'
import { AuthenticationService } from '../../../services/authentication.service'
import { ToastrService } from 'ngx-toastr'

@Component({
    selector: 'app-admin-global-variables',
    templateUrl: './admin-global-variables.component.html',
    styleUrls: ['./admin-global-variables.component.scss'],
})
export class AdminGlobalVariablesComponent implements OnInit {
    variablesForm: FormGroup
    loading = true
    saving = false
    globalVariables: GlobalVariables

    constructor(
        private fb: FormBuilder,
        private globalVariablesService: GlobalVariablesService,
        private auth: AuthenticationService,
        private toastr: ToastrService
    ) {
        this.variablesForm = this.fb.group({
            profitPercentage: [10, [Validators.required, Validators.min(0), Validators.max(100)]],
            averageBayPrice: [1000, [Validators.required, Validators.min(0)]],
            paygateGatewayFeePercentage: [3.5, [Validators.required, Validators.min(0), Validators.max(100)]],
            eftGatewayFeePercentage: [2, [Validators.required, Validators.min(0), Validators.max(100)]],
            averageMonths: [5, [Validators.required, Validators.min(1)]],
            averageBaysPerSub: [3, [Validators.required, Validators.min(1)]],
        })
    }

    async ngOnInit(): Promise<void> {
        await this.loadVariables()
    }

    async loadVariables(): Promise<void> {
        this.loading = true
        try {
            this.globalVariables = await this.globalVariablesService.loadGlobalVariables()
            this.variablesForm.patchValue({
                profitPercentage: this.globalVariables.profitPercentage,
                averageBayPrice: this.globalVariables.averageBayPrice,
                paygateGatewayFeePercentage: this.globalVariables.paygateGatewayFeePercentage,
                eftGatewayFeePercentage: this.globalVariables.eftGatewayFeePercentage,
                averageMonths: this.globalVariables.averageMonths,
                averageBaysPerSub: this.globalVariables.averageBaysPerSub,
            })
        } catch (error) {
            console.error('Error loading variables:', error)
            this.toastr.error('Failed to load global variables')
        } finally {
            this.loading = false
        }
    }

    async onSubmit(): Promise<void> {
        if (this.variablesForm.invalid) {
            return
        }

        this.saving = true
        try {
            const values = this.variablesForm.value
            const updatedVariables = new GlobalVariables({
                profitPercentage: values.profitPercentage,
                averageBayPrice: values.averageBayPrice,
                paygateGatewayFeePercentage: values.paygateGatewayFeePercentage,
                eftGatewayFeePercentage: values.eftGatewayFeePercentage,
                averageMonths: values.averageMonths,
                averageBaysPerSub: values.averageBaysPerSub,
            })

            await this.globalVariablesService.saveGlobalVariables(updatedVariables, this.auth.user?.email || 'unknown')
            this.globalVariables = updatedVariables
            this.toastr.success('Global variables saved successfully')
        } catch (error) {
            console.error('Error saving variables:', error)
            this.toastr.error('Failed to save global variables')
        } finally {
            this.saving = false
        }
    }

    // Computed values for preview
    get averageSubscriptionValue(): number {
        const values = this.variablesForm.value
        return values.averageBayPrice * values.averageMonths * values.averageBaysPerSub
    }

    get profitPerSubscription(): number {
        const values = this.variablesForm.value
        return (this.averageSubscriptionValue * values.profitPercentage) / 100
    }

    get highValueThreshold(): number {
        return this.averageSubscriptionValue * 2
    }

    get lowValueThreshold(): number {
        return this.averageSubscriptionValue * 0.5
    }
}

